import React from 'react';
import ChartUpIcon from 'images/dashboard/chart-up.svg';
import ChartDownIcon from 'images/dashboard/chart-down.svg';
import { getValueByPath, formatValue } from './metaUtils';
import { Icon } from '@iconify/react';

// Renders an inline row of highlight items below ChartMeta
// Config shape:
// inlineMeta: {
//   items: [
//     {
//       icon?: 'green-chart-up'|'chart-up'|'red-chart-down'|'chart-down',
//       heading: { path?, default?, format?: 'number'|'currency'|'percent'|'string', prefix?, suffix?, maximumFractionDigits?, color?: string },
//       subtitle?: { path?, default?, format?: 'string', prefix?, suffix?, maximumFractionDigits?, color?: string }
//     }
//   ]
// }
export default function ChartInlineMeta({ inlineMeta, dataContext }) {
    if (!inlineMeta || !Array.isArray(inlineMeta.items) || inlineMeta.items.length === 0) {
        return null;
    }

    const iconMap = {
        'green-chart-up': ChartUpIcon,
        'chart-up': ChartUpIcon,
        'red-chart-down': ChartDownIcon,
        'chart-down': ChartDownIcon,
    };

    const resolveField = (cfg, defaultFormat) => {
        const normalized = (typeof cfg === 'string' || typeof cfg === 'number') ? { default: cfg } : (cfg || {});
        const path = typeof normalized.path === 'string' ? normalized.path : undefined;
        const fallback = normalized.hasOwnProperty('default') ? normalized.default
            : (normalized.hasOwnProperty('value') ? normalized.value
                : (normalized.hasOwnProperty('text') ? normalized.text : undefined));
        const raw = path ? getValueByPath(dataContext, path) : undefined;
        const effective = (raw !== undefined && raw !== null) ? raw : fallback;
        const format = normalized.format || defaultFormat;
        const prefix = normalized.prefix || '';
        const suffix = normalized.suffix || '';
        const maximumFractionDigits = normalized.maximumFractionDigits ?? 2;
        const display = formatValue(effective, format, { prefix, suffix, maximumFractionDigits });
        const hasValue = effective !== undefined && effective !== null && String(display).length > 0;
        const color = typeof normalized.color === 'string' ? normalized.color : '';
        return { display, hasValue, color };
    };

    const toEightDigitHex = (hex, alphaHex = '15') => {
        if (typeof hex !== 'string') return '#6B7280' + alphaHex;
        if (/^#([0-9a-fA-F]{8})$/.test(hex)) return hex;
        if (/^#([0-9a-fA-F]{6})$/.test(hex)) return `${hex}${alphaHex}`;
        return '#6B7280' + alphaHex;
    };

    return (
        <div className={`flex items-center gap-5 mt-2`}>
            {inlineMeta.items.map((item, idx) => {
                const iconKey = typeof item?.icon === 'string' ? item.icon : undefined;
                const iconSrc = iconKey && iconMap[iconKey] ? iconMap[iconKey] : null;

                const { display: heading, hasValue: hasHeading, color: headingColor } = resolveField(item.heading, 'number');
                const { display: subtitle, hasValue: hasSubtitle, color: subtitleColor } = resolveField(item.subtitle, 'string');

                // Skip rendering if both heading and subtitle are empty
                if (!hasHeading && !hasSubtitle) return null;

                return (
                    <div key={item.id || idx} className={`flex items-start gap-2 mt-5`}>
                        {iconKey ? (
                            <div
                                className={`p-2 rounded-xl border`}
                                style={{ backgroundColor: toEightDigitHex(headingColor) }}
                            >
                                <Icon
                                    icon={iconKey}
                                    width={20}
                                    height={20}
                                    strokeWidth={1}
                                    style={{ color: headingColor || '#6B7280' }}
                                />
                            </div>
                        ) : null}
                        <div className={`flex flex-col items-start`}>
                            {hasHeading ? (
                                <h3 className={`text-2xl font-semibold`} style={{ color: headingColor || undefined }}>{heading}</h3>
                            ) : null}
                            {hasSubtitle ? (
                                <p className={`text-sm font-normal`} style={{ color: subtitleColor || undefined }}>{subtitle}</p>
                            ) : null}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}


